import { useEffect, useState } from 'react'

import { ActionIcon, Collapse, Loader, Text, Tooltip } from '@mantine/core'
import { ChevronDown, ChevronRight, Eye, EyeOff } from 'lucide-react'

import { useQuery } from '@redwoodjs/web'

import CopyableId from 'src/components/DataDisplay/CopyableId/CopyableId'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_VOE_APPLICANT_INFO } from 'src/graphql/voe.graphql'
import IconCopy from 'src/icons/IconCopy'
import { formatDateSimple } from 'src/lib/formatters'
import { formatSsnString, unformatSsn } from 'src/lib/ssn.utils'

// Single label / value row with an optional copy button.
const InfoRow = ({
  label,
  value,
  copyLabel,
  copyValue,
  children,
}: {
  label: string
  value: string
  copyLabel?: string
  copyValue?: string
  children?: React.ReactNode
}) => {
  const hasValue = !!copyValue
  return (
    <div className="flex flex-row items-center justify-between">
      <Text size="xs" fw={300}>
        {label}
      </Text>
      <div className="flex items-center gap-1">
        <Text size="xs" fw={300}>
          {value}
        </Text>
        {children}
        {hasValue && (
          <CopyableId value={copyValue} label={copyLabel ?? label} />
        )}
      </div>
    </div>
  )
}

// ── Applicant Info Section (collapsible) ────────────────────────────────
// Applicant identity is fetched via its own query (keyed by applicantId),
// lazily while the drawer is open — decoupled from the VoE record so its
// PII isn't loaded with the verification list.
const ApplicantInfoSection = ({
  applicantId,
  opened,
}: {
  applicantId?: string
  opened: boolean
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isSsnVisible, setIsSsnVisible] = useState(false)

  // Re-mask the SSN whenever the drawer opens/closes or switches applicants.
  // VoEDetails stays mounted while selectedVoe changes on the verifications
  // table, so without this a prior "Show SSN" would leak the next applicant's.
  useEffect(() => {
    setIsSsnVisible(false)
  }, [applicantId, opened])

  const { data, loading } = useQuery(GET_VOE_APPLICANT_INFO, {
    variables: { id: applicantId },
    skip: !opened || !applicantId,
  })

  const applicant = data?.applicant
  const application = applicant?.application
  const firstName = applicant?.firstName || '-'
  const lastName = applicant?.lastName || '-'
  const dob = application?.dob ? formatDateSimple(application.dob) : '-'

  const rawSsn = application?.ssn ? unformatSsn(application.ssn) : ''
  const formattedSsn = rawSsn
    ? formatSsnString(application?.issuingCountry, rawSsn)
    : ''

  return (
    <>
      <button
        className="flex items-center gap-1 text-doubleNickel-gray-500 hover:text-doubleNickel-gray-700"
        onClick={() => setIsExpanded((v) => !v)}
      >
        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Text size="xs" fw={400} className="text-inherit">
          APPLICANT INFO
        </Text>
      </button>

      <Collapse in={isExpanded}>
        {loading ? (
          <div className="flex justify-center py-2">
            <Loader size="xs" />
          </div>
        ) : (
          <div className="flex flex-col space-y-4">
            <InfoRow
              label="First Name"
              value={firstName}
              copyValue={applicant?.firstName || undefined}
            />
            <InfoRow
              label="Last Name"
              value={lastName}
              copyValue={applicant?.lastName || undefined}
            />
            <InfoRow
              label="Date of Birth"
              value={dob}
              copyLabel="Date of Birth"
              copyValue={dob !== '-' ? dob : undefined}
            />
            {/* SSN row — masked by default with a reveal toggle. The copy
                button and its tooltip never expose the value so the SSN stays
                hidden until explicitly revealed. */}
            <div className="flex flex-row items-center justify-between">
              <Text size="xs" fw={300}>
                SSN
              </Text>
              <div className="flex items-center gap-1">
                <Text size="xs" fw={300}>
                  {formattedSsn
                    ? isSsnVisible
                      ? formattedSsn
                      : '•••-••-••••'
                    : 'Not Available'}
                </Text>
                {formattedSsn && (
                  <>
                    <Tooltip
                      label={isSsnVisible ? 'Hide SSN' : 'Show SSN'}
                      withArrow
                    >
                      <ActionIcon
                        variant="subtle"
                        size={18}
                        color="gray"
                        onClick={() => setIsSsnVisible((v) => !v)}
                      >
                        {isSsnVisible ? (
                          <EyeOff className="h-3.5 w-3.5 stroke-doubleNickel-gray-500" />
                        ) : (
                          <Eye className="h-3.5 w-3.5 stroke-doubleNickel-gray-500" />
                        )}
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Copy SSN" withArrow>
                      <ActionIcon
                        variant="subtle"
                        size={18}
                        color="gray"
                        onClick={(event) => {
                          event.stopPropagation()
                          navigator?.clipboard?.writeText?.(formattedSsn)
                          toast('SSN copied to clipboard', 'success')
                        }}
                      >
                        <IconCopy className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
                      </ActionIcon>
                    </Tooltip>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </Collapse>
    </>
  )
}

export default ApplicantInfoSection
